// NonConformityType Configuration - UNIFIED APPROACH
// Manages NCR types (Product, Process, System, Documentation, etc.)
// Based on Prisma NonConformityType model schema

import { createTrackingConfig } from '../../../components/generic/labels';
import { tableConfig, uiConfig } from '../../../config/appConfig';

export const nonConformityTypeConfig = {
  entityName: 'NonConformityType',
  
  api: {
    baseEndpoint: '/non-conformity-type',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      activate: (id) => `/${id}/activate`,
      deactivate: (id) => `/${id}/deactivate`,
      reorder: '/reorder'
    },
    lookupEndpoints: {}
  },
  
  ui: {
    drawerWidth: '600px',
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle
  },
  
  service: {
    name: 'nonConformityTypeService',
    methods: {
      getAll: 'getAllNonConformityTypes',
      getById: 'getNonConformityTypeById', 
      create: 'createNonConformityType',
      update: 'updateNonConformityType',
      delete: 'deleteNonConformityType',
      validate: 'validateData'
    }
  },

  fields: [
    { name: 'id', label: 'ID', type: 'hidden', table: false, filterable: false },
    { 
      name: 'name', 
      label: 'Type Name', 
      type: 'text', 
      required: true,
      placeholder: 'Enter NCR type name (e.g., Product, Process, System, Documentation)',
      table: { header: 'Type Name', width: '200px', sortable: true },
      filterable: true,
      validation: { 
        required: true, 
        minLength: 2, 
        maxLength: 100,
        pattern: /^[A-Za-z\s&\-'.()]+$/,
        message: 'Type name must contain only letters, spaces, and common punctuation'
      }
    },
    { 
      name: 'description', 
      label: 'Description', 
      type: 'textarea', 
      required: false,
      placeholder: 'Describe when this NCR type should be used',
      table: { header: 'Description', width: '250px', sortable: false },
      filterable: true,
      validation: { 
        required: false, 
        maxLength: 500
      },
      rows: 3,
      helperText: 'Brief description of when to use this NCR type'
    },
    { 
      name: 'isActive', 
      label: 'Active', 
      type: 'switch', 
      required: false,
      defaultValue: true,
      table: { header: 'Status', width: '100px', sortable: true, cellRenderer: 'boolean' },
      filterable: true,
      validation: { required: false },
      switchLabel: 'Type is active and available for use',
      description: 'Inactive types cannot be used for new NCR records'
    },
    { 
      name: 'record_order', 
      label: 'Display Order', 
      type: 'number', 
      required: false,
      placeholder: 'Enter display order (optional)',
      table: { header: 'Order', width: '80px', sortable: true },
      filterable: false,
      validation: { required: false, min: 1, max: 999, integer: true },
      helperText: 'Controls the order in dropdown lists and displays',
      step: 1, decimals: 0, allowNegative: false
    },
    { 
      name: 'color', 
      label: 'Display Color', 
      type: 'color', 
      required: false,
      placeholder: 'Choose a color for visual categorization',
      table: { header: 'Color', width: '100px', sortable: false, cellRenderer: 'color' },
      filterable: false,
      validation: { 
        required: false,
        pattern: /^(#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})|[a-z]+)$/,
        message: 'Please enter a valid color (hex code or color name)'
      },
      helperText: 'Used for visual categorization in lists and reports'
    }
  ],

  get tableColumns() {
    return this.fields.filter(field => field.table !== false && !field.hidden)
      .map(field => ({
        key: field.name,
        header: field.table?.header || field.label,
        width: field.table?.width || 'auto',
        sortable: field.table?.sortable !== false,
        displayField: field.table?.displayField || field.name,
        cellRenderer: field.table?.cellRenderer || 'default'
      }));
  },

  filterableFields: ['name', 'description', 'isActive'],
  filterGroups: [
    { name: 'basic', label: 'Basic Filters', fields: ['name', 'isActive'] },
    { name: 'advanced', label: 'Advanced Filters', fields: ['description', 'record_order'] }
  ],

  customSortOrders: { isActive: [true, false], record_order: 'asc' },

  messages: {
    loading: 'Loading NCR types...',
    noData: 'No NCR types found. Click "Add NCR Type" to create the first type.',
    loadError: 'Failed to load NCR types',
    saveSuccess: 'NCR type saved successfully!',
    saveError: 'Failed to save NCR type',
    deleteSuccess: 'NCR type deleted successfully!',
    deleteError: 'Failed to delete NCR type',
    deleteConfirm: 'Are you sure you want to delete this NCR type? This action cannot be undone and may affect NCR records that reference this type.'
  },

  layout: {
    mode: 'sections',
    sections: [
      {
        id: 'basic',
        title: 'NCR Type Information',
        icon: '🏷️',
        columns: 2,
        fields: [{ name: 'name' }, { name: 'isActive' }, { name: 'record_order' }, { name: 'color' }]
      },
      {
        id: 'details',
        title: 'Additional Details',
        icon: '📝',
        columns: 1,
        fields: [{ name: 'description' }]
      }
    ]
  },

  trackingSection: createTrackingConfig('NonConformityType'),

  labels: {
    singular: 'NCR Type',
    plural: 'NCR Types',
    addButton: 'Add NCR Type',
    editButton: 'Edit NCR Type',
    deleteButton: 'Delete NCR Type',
    pageTitle: 'NCR Type Management',
    pageSubtitle: 'Manage non-conformity report types for categorization',
    searchPlaceholder: 'Search NCR types by name...',
    basicInfoSection: 'NCR Type Information'
  },

  fieldsToRemove: ['id', 'createdAt', 'updatedAt', 'createdById', 'updatedById', 'nonConformityReports'],
  exportFileName: 'ncr-types',

  transformData: (data) => {
    const transformed = { ...data };
    if (transformed.record_order !== undefined) {
      if (transformed.record_order === '' || transformed.record_order === null) {
        delete transformed.record_order;
      } else {
        const numValue = parseInt(transformed.record_order, 10);
        if (!isNaN(numValue)) transformed.record_order = numValue;
        else delete transformed.record_order;
      }
    }
    return transformed;
  },

  validateField: null
};
